import { Background, BackgroundColor, Container, Spacer, SpacerVariant, spacerOptions } from "../components/layout"
import { H1Icon, H2Icon, H3Icon, H4Icon } from "../components/svg"
import React, { useRef } from "react"
import {
    Repeater,
    RichTextExt,
    Text,
    blockPluginConstructor,
    types,
    useAdminContext,
    usePageValues,
} from "react-bricks/frontend"

import { ConditionalWrapper } from "../components/util"
import { Icons } from "../../types"
import { PDPImageProps } from "./pdp/Image"
import { Product } from "../../shared/sdk/types"
import { TypographyElement } from "../Typography"
import classNames from "classnames"
import { formatPrice } from "../../shared/utils/format"
import i18n from "../../i18n/config"
import { price } from "../../util/price"
import { twMerge } from "tailwind-merge"
import { useCheckForEmptyText } from "../../hooks/use-check-for-empty-text"
import { useLocalesState } from "../../shared/context/locales"
import { useMediaQuery } from "../../hooks/use-media-query-admin"
import { usePageContext } from "../../context/page"
import { useQuery } from "@tanstack/react-query"
import { useSDK } from "../../shared/context/client"
import { useSlider } from "../../shared/hooks/use-slider"

export interface PDPProps {
    spacerTop: boolean
    spacerBottom: boolean
    variantTop?: SpacerVariant
    variantBottom?: SpacerVariant
    id?: string
    image?: {
        id: string
        props: PDPImageProps
        type: "PDPImage"
    }[]
    subHeadline?: TypographyElement[]
    label?: TypographyElement[]
    unit?: TypographyElement[]
    badge?: TypographyElement[]
    sku?: string
    activeTab?: number
    inline?: boolean
    index?: number
    products?: Product[]
    realPrice?: boolean
}

const customAdditional = (language: string, product?: Product, realPrice?: boolean) => {
    if (!product) return

    return product.sku === "LV2START" && !realPrice
        ? i18n.t("lvstart_pricing_addition_extended", {
              total_price: formatPrice(product.price.current.taxed, product.price.currency.name, language),
          })
        : product.sku === "LV500"
        ? i18n.t("price_per_serving", {
              price: formatPrice(product.price.basic.taxed / 100, product.price.currency.name, language),
          })
        : undefined
}

const customSmallprint = (language: string, product?: Product) => {
    if (!product) return

    return !["Liter", "litre"].includes(product.price.basic.unit)
        ? i18n.t("price_incl_vat")
        : i18n.t("price_per_litre", {
              price: formatPrice(product.price.basic.taxed, product.price.currency.name, language),
          })
}

const PDP: types.Brick<PDPProps> = ({
    spacerTop,
    spacerBottom,
    variantTop = SpacerVariant.Small,
    variantBottom = SpacerVariant.Small,
    id,
    image,
    badge,
    unit,
    subHeadline,
    label,
    sku,
    products: productsProps,
    activeTab,
    inline,
    index,
    realPrice,
}) => {
    const { language, region } = useLocalesState()

    const isMobile = useMediaQuery("(max-width: 767px)", { initializeWithValue: false })

    const sliderRef = useRef<null | HTMLDivElement>(null)

    const length = image ? image.length : 0
    const gap = isMobile ? 6 : 24

    const {
        handleDragStart,
        handleDragMove,
        handleDragEnd,
        index: sliderIndex,
        lastIndex,
        transition,
        goTo,
    } = useSlider(length, sliderRef, 0)

    const emptySubHeadline = useCheckForEmptyText(subHeadline)
    const emptyLabel = useCheckForEmptyText(label)
    const emptyUnit = useCheckForEmptyText(unit)
    const emptyBadge = useCheckForEmptyText(badge)

    const { products: productsBuildTime, page: pageContext } = usePageContext()

    const client = useSDK()

    const [pageValues] = usePageValues()

    const page = pageContext || pageValues

    const { data } = useQuery({
        queryKey: ["products", sku, region, page.language],
        queryFn: ({ signal }) => client.getProducts({ sku: sku, country: region }, page.language, signal),
        enabled: !!(sku && !productsProps),
        meta: { background: true },
    })

    if (typeof index !== "undefined" && activeTab !== index) return null

    let products = productsProps

    if (!products) {
        products = data || productsBuildTime
    }

    const product = products?.find(p => p.sku === sku)

    const additional = customAdditional(language, product, realPrice)

    const { isAdmin } = useAdminContext()

    const heading1 = blockPluginConstructor({
        name: "heading1",
        label: "h1",
        hotKey: "mod+shift+1",
        render: ({ children }) => <h1>{children}</h1>,
        icon: H1Icon,
    })

    const heading2 = blockPluginConstructor({
        name: "heading2",
        label: "h2",
        hotKey: "mod+shift+2",
        render: ({ children }) => <h2>{children}</h2>,
        icon: H2Icon,
    })

    const heading3 = blockPluginConstructor({
        name: "heading3",
        label: "h3",
        hotKey: "mod+shift+3",
        render: ({ children }) => <h3>{children}</h3>,
        icon: H3Icon,
    })

    const heading4 = blockPluginConstructor({
        name: "heading4",
        label: "h4",
        hotKey: "mod+shift+4",
        render: ({ children }) => <h4>{children}</h4>,
        icon: H4Icon,
    })

    if (sku && !product && !isAdmin) return null

    return (
        <div key={index}>
            <ConditionalWrapper
                condition={!inline}
                wrapper={children => (
                    <Background color={BackgroundColor.White} className="overflow-hidden">
                        <Container>
                            <Spacer variant={!spacerTop ? SpacerVariant.None : variantTop} />
                            {children}
                            <Spacer variant={!spacerBottom ? SpacerVariant.None : variantBottom} />
                        </Container>
                    </Background>
                )}
            >
                <div
                    className="grid grid-cols-1 sm:grid-cols-[320px_auto] sm:gap-x-10 md:grid-cols-[462px_auto] lg:grid-cols-[530px_auto] lg:gap-x-16 xl:grid-cols-[708px_auto] xl:gap-x-20"
                    id={id}
                >
                    <div>
                        <div className="-mx-3 sm:mx-0 sm:overflow-hidden">
                            <ConditionalWrapper
                                wrapper={children => (
                                    <div
                                        className={classNames("grid gap-x-1.5 sm:gap-x-6", {
                                            "transition-transform duration-[250ms] ease-in-out": transition,
                                            "cursor-grab active:cursor-grabbing": length > 1,
                                        })}
                                        style={{
                                            width: `calc(100% * ${length} + ${gap}px * ${length - 1})`,
                                            transform: `translateX(calc(${
                                                -1 * sliderIndex
                                            } * (100% + ${gap}px) / ${length}))`,
                                            gridTemplateColumns: `repeat(${length}, 1fr)`,
                                        }}
                                        onTouchStart={handleDragStart}
                                        onMouseDown={handleDragStart}
                                        onTouchMove={handleDragMove}
                                        onMouseMove={handleDragMove}
                                        onTouchEnd={handleDragEnd}
                                        onMouseUp={handleDragEnd}
                                        onMouseLeave={handleDragEnd}
                                        role="none"
                                        ref={sliderRef}
                                    >
                                        {children}
                                    </div>
                                )}
                                condition={length > 1}
                            >
                                <Repeater propName="image" />
                            </ConditionalWrapper>
                        </div>
                        {length > 1 && (
                            <div className="mt-3">
                                <div className="flex justify-center">
                                    {[...Array(length)].map((_, i) => (
                                        // eslint-disable-next-line
                                        <button
                                            type="button"
                                            className={classNames(
                                                "mr-1.5 h-[9px] w-[9px] rounded-full bg-blue last:mr-0",
                                                {
                                                    "opacity-30": lastIndex !== i,
                                                }
                                            )}
                                            onClick={() => goTo(i)}
                                            key={i}
                                        ></button>
                                    ))}
                                </div>
                                <div className="mt-5 hidden justify-center sm:flex lg:mt-7">
                                    <button
                                        className={classNames("mr-3.5 hidden select-none text-blue sm:block lg:mr-5", {
                                            "pointer-events-none opacity-30": lastIndex < 1,
                                        })}
                                        type="button"
                                        onClick={() => goTo(lastIndex - 1)}
                                    >
                                        <i className="lavita-icon text-[42px] lg:text-[52px]">{Icons.back}</i>
                                    </button>
                                    <button
                                        className={classNames("hidden select-none text-blue sm:block", {
                                            "pointer-events-none opacity-30": lastIndex + 1 >= length,
                                        })}
                                        type="button"
                                        onClick={() => goTo(lastIndex + 1)}
                                    >
                                        <i className="lavita-icon scale-x-[-1] text-[42px] lg:text-[52px]">
                                            {Icons.back}
                                        </i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="mt-7 flex flex-col sm:mt-5">
                        <div className="flex justify-between">
                            {!emptySubHeadline && (
                                <div className="mb-2 grow text-xs sm:mb-3 lg:text-sm">
                                    <RichTextExt
                                        propName="subHeadline"
                                        plugins={[heading1, heading2, heading3, heading4]}
                                        placeholder="Lorem ipsum"
                                    />
                                </div>
                            )}
                            {!emptyLabel && (
                                <div className="shrink-0 grow">
                                    <Text
                                        propName="label"
                                        renderBlock={({ children }) => (
                                            <p className="mb-2 ml-auto text-right text-xs sm:mb-3 lg:text-sm">
                                                {children}
                                            </p>
                                        )}
                                        placeholder="Lorem ipsum"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex justify-between border-b border-background-tide pb-5 sm:pb-7 lg:pb-10">
                            <div className="flex grow flex-col">
                                <div className="text-xl font-bold md:text-3xl lg:text-5xl">
                                    <RichTextExt
                                        propName="headline"
                                        plugins={[heading1, heading2, heading3, heading4]}
                                        placeholder="Lorem ipsum"
                                    />
                                </div>
                                <Text
                                    propName="description"
                                    renderBlock={({ children }) => (
                                        <p className="mt-2 text-lg sm:mt-3 md:text-xl lg:mt-3.5 lg:text-2xl">
                                            {children}
                                        </p>
                                    )}
                                    placeholder="Lorem ipsum dolor sit amet"
                                />
                            </div>
                            <div className="flex shrink-0 grow flex-col text-right">
                                {product ? (
                                    <>
                                        <strong className="text-xl font-bold md:text-3xl lg:text-5xl">
                                            {!emptyUnit && (
                                                <Text
                                                    propName="unit"
                                                    renderBlock={({ children }) => (
                                                        <span className="text-sm sm:text-base md:text-md lg:text-lg">
                                                            {children}
                                                        </span>
                                                    )}
                                                    placeholder="Lorem"
                                                />
                                            )}
                                            {price(language, product, realPrice)}
                                        </strong>
                                        {additional ? (
                                            <div className="mt-2 sm:mt-3 lg:mt-3.5">
                                                <span className="inline-flex flex-wrap whitespace-pre-wrap rounded-full bg-background-tide px-2.5 py-1.5 text-xs font-bold sm:px-3 lg:text-sm">
                                                    <p>{additional}</p>
                                                </span>
                                            </div>
                                        ) : !emptyBadge ? (
                                            <div className="mt-2 sm:mt-3 lg:mt-3.5">
                                                <span
                                                    className={twMerge(
                                                        classNames(
                                                            "flex-wrap whitespace-pre-wrap rounded-full bg-background-tide px-2.5 py-1.5 text-xs font-bold sm:px-3 lg:text-sm",
                                                            {
                                                                "rounded-sm p-2 sm:p-2.5 lg:px-3.5 lg:py-3":
                                                                    badge && badge.length > 1,
                                                                "inline-flex":
                                                                    badge?.length &&
                                                                    badge[0].children?.length &&
                                                                    badge[0].children[0].text,
                                                            }
                                                        )
                                                    )}
                                                >
                                                    <Text
                                                        propName="badge"
                                                        multiline
                                                        renderBlock={({ children }) => <p>{children}</p>}
                                                        placeholder="Lorem ipsum dolor"
                                                    />
                                                </span>
                                            </div>
                                        ) : null}
                                        <small className="mt-2 text-xs text-dark-grey sm:mt-3 lg:mt-3.5 lg:text-sm">
                                            {customSmallprint(language, product)}
                                        </small>
                                    </>
                                ) : (
                                    <>
                                        <strong className="text-xl font-bold md:text-3xl lg:text-5xl">
                                            {!emptyUnit && (
                                                <Text
                                                    propName="unit"
                                                    renderBlock={({ children }) => (
                                                        <span className="text-sm sm:text-base md:text-md lg:text-lg">
                                                            {children}
                                                        </span>
                                                    )}
                                                    placeholder="Lorem"
                                                />
                                            )}
                                            <Text
                                                propName="price"
                                                renderBlock={({ children }) => <>{children}</>}
                                                placeholder="Lorem"
                                            />
                                        </strong>
                                        {!emptyBadge && (
                                            <div className="mt-2 sm:mt-3 lg:mt-3.5">
                                                <span
                                                    className={twMerge(
                                                        classNames(
                                                            "flex-col whitespace-pre-wrap rounded-full bg-background-tide px-2.5 py-1.5 text-xs font-bold sm:px-3 lg:text-sm",
                                                            {
                                                                "rounded-sm p-2 sm:p-2.5 lg:px-3.5 lg:py-3":
                                                                    badge && badge.length > 1,
                                                                "inline-flex":
                                                                    badge?.length &&
                                                                    badge[0].children?.length &&
                                                                    badge[0].children[0].text,
                                                            }
                                                        )
                                                    )}
                                                >
                                                    <Text
                                                        propName="badge"
                                                        multiline
                                                        renderBlock={({ children }) => <p>{children}</p>}
                                                        placeholder="Lorem ipsum dolor"
                                                    />
                                                </span>
                                            </div>
                                        )}
                                        <Text
                                            propName="smallprint"
                                            renderBlock={({ children }) => (
                                                <small className="mt-2 text-xs text-dark-grey sm:mt-3 lg:mt-3.5 lg:text-sm">
                                                    {children}
                                                </small>
                                            )}
                                            placeholder="Lorem ipsum dolor sit"
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                        <Repeater
                            propName="repeater"
                            renderWrapper={children => (
                                <div className="mt-5 flex flex-col gap-5 sm:mt-7 lg:mt-10 lg:gap-7">{children}</div>
                            )}
                            itemProps={{ fullWidth: true }}
                        />
                    </div>
                </div>
            </ConditionalWrapper>
        </div>
    )
}

PDP.schema = {
    name: "PDP",
    label: "PDP",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/PDP.png`,
    repeaterItems: [
        {
            name: "image",
            items: [
                {
                    type: "PDPImage",
                    label: "Image",
                    min: 1,
                },
            ],
        },
        {
            name: "repeater",
            items: [
                {
                    type: "PDPIconTextGrid",
                    label: "Icon Text Grid",
                    min: 0,
                },
                {
                    type: "Button",
                    label: "Button",
                    min: 0,
                },
                {
                    type: "GreenText",
                    label: "Green Text",
                    min: 0,
                },
                {
                    type: "List",
                    label: "List",
                    min: 0,
                },
                {
                    type: "Trust",
                    label: "Trust Badge",
                    min: 0,
                    max: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "sku",
            label: "SKU",
            type: types.SideEditPropType.Text,
        },
        {
            name: "id",
            label: "ID",
            type: types.SideEditPropType.Text,
        },
        {
            name: "realPrice",
            label: "Real price",
            type: types.SideEditPropType.Boolean,
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantTop",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: [...spacerOptions].sort((a, b) => (a.value === SpacerVariant.Small ? -1 : 1)),
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerTop === true,
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Boolean,
                },
                {
                    name: "variantBottom",
                    label: "Spacer Variant",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: [...spacerOptions].sort((a, b) => (a.value === SpacerVariant.Small ? -1 : 1)),
                        display: types.OptionsDisplay.Select,
                    },
                    show: props => props.spacerBottom === true,
                },
            ],
        },
    ],
    getDefaultProps: (): PDPProps => ({
        realPrice: false,
        spacerTop: true,
        spacerBottom: true,
        variantTop: SpacerVariant.Small,
        variantBottom: SpacerVariant.Small,
    }),
}

export default PDP
